/**
 * Shortlist Page
 *
 * Replaces WishlistPage. Reads from shortlistStore (localStorage).
 * Users can add items to inquiry basket or remove them.
 * No auth required.
 */

import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  InlineGrid,
  Button,
  Box,
  Banner,
} from '@shopify/polaris';
import { DeleteIcon } from '@shopify/polaris-icons';
import { useShortlistStore } from '../store/shortlistStore';
import { useInquiryStore } from '../store/inquiryStore';
import { useUIStore } from '../store/uiStore';

export function ShortlistPage() {
  const navigate = useNavigate();
  const { items, removeItem, clearAll } = useShortlistStore();
  const { addItem: addToBasket } = useInquiryStore();
  const { showToast } = useUIStore();

  const handleAddToBasket = (item: typeof items[0]) => {
    addToBasket({
      productId: item.productId,
      productName: item.name,
      productSlug: item.slug,
      productImage: item.image,
      quantity: 20,
      color: null,
      type: 'BULK',
    });
    showToast(`${item.name} added to inquiry basket`, 'success');
  };

  const handleAddAllToBasket = () => {
    items.forEach((item) => {
      addToBasket({
        productId: item.productId,
        productName: item.name,
        productSlug: item.slug,
        productImage: item.image,
        quantity: 20,
        color: null,
        type: 'BULK',
      });
    });
    showToast(`${items.length} items added to inquiry basket`, 'success');
  };

  if (items.length === 0) {
    return (
      <Page title="Your Shortlist" narrowWidth>
        <Card>
          <BlockStack gap="400" inlineAlign="center">
            <Text as="h2" variant="headingMd">Your shortlist is empty</Text>
            <Text as="p" tone="subdued">
              Browse products and tap the heart icon to shortlist items for comparison.
            </Text>
            <Button variant="primary" onClick={() => navigate('/categories')}>
              Browse Products
            </Button>
          </BlockStack>
        </Card>
      </Page>
    );
  }

  return (
    <Page
      title="Your Shortlist"
      subtitle={`${items.length} item${items.length !== 1 ? 's' : ''}`}
      primaryAction={{
        content: 'Add All to Basket',
        onAction: handleAddAllToBasket,
      }}
      secondaryActions={[
        {
          content: 'Clear All',
          destructive: true,
          onAction: clearAll,
        },
      ]}
    >
      <Layout>
        <Layout.Section>
          <Banner tone="info">
            Shortlisted products are stored in your browser. Add them to your inquiry basket when you are ready to get a quote.
          </Banner>
        </Layout.Section>

        <Layout.Section>
          <InlineGrid columns={{ xs: 1, sm: 2, md: 3, lg: 4 }} gap="400">
            {items.map((item) => (
              <Card key={item.productId}>
                <BlockStack gap="300">
                  {item.image ? (
                    <div style={{ borderRadius: 8, overflow: 'hidden' }}>
                      <img
                        src={item.image}
                        alt={item.name}
                        style={{ width: '100%', height: 160, objectFit: 'cover' }}
                      />
                    </div>
                  ) : (
                    <Box minHeight="160px" background="bg-surface-secondary" borderRadius="200" />
                  )}

                  <Text as="h3" variant="headingSm">{item.name}</Text>

                  <InlineStack gap="200">
                    <Button
                      size="slim"
                      variant="primary"
                      onClick={() => handleAddToBasket(item)}
                    >
                      Add to Basket
                    </Button>
                    <Button
                      size="slim"
                      onClick={() => navigate(`/products/${item.slug}`)}
                    >
                      View
                    </Button>
                    <Button
                      size="slim"
                      icon={DeleteIcon}
                      variant="plain"
                      tone="critical"
                      onClick={() => removeItem(item.productId)}
                      accessibilityLabel="Remove from shortlist"
                    />
                  </InlineStack>
                </BlockStack>
              </Card>
            ))}
          </InlineGrid>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
